import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signUp } from '../../../services/auth';
import { useAuth } from '../../../contexts/AuthContext';
import { ArrowRight } from 'lucide-react';

const SignUp = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const isSigningUpRef = useRef(false);

  // Redirect if already authenticated (but not during signup process)
  useEffect(() => {
    if (user && !authLoading && !loading && !isSigningUpRef.current) {
      navigate('/host/dashboard', { replace: true });
    }
  }, [user, authLoading, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    isSigningUpRef.current = true;

    try {
      await signUp(email, password, displayName);
      // After sign-up, auto-set role to host and redirect to dashboard
      const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../../../firebase/config');
      const { getAuth } = await import('firebase/auth');
      
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (currentUser) {
        // Set role to host automatically for host app
        await setDoc(
          doc(db, 'users', currentUser.uid),
          {
            role: 'host',
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        
        // Wait a bit for Firestore to update, then refresh user data
        await new Promise(resolve => setTimeout(resolve, 500));
        await refreshUser();
        
        // Small delay to ensure state updates propagate
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Force a full page refresh to ensure all state is properly loaded
        // This prevents blank white pages and ensures the modal shows correctly
        window.location.href = '/host/dashboard';
      } else {
        setLoading(false);
        isSigningUpRef.current = false;
      }
    } catch (err) {
      let errorMessage = 'Failed to create account. Please try again.';
      
      if (err.code) {
        switch (err.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'This email is already registered. Please sign in instead.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Please enter a valid email address.';
            break;
          case 'auth/weak-password':
            errorMessage = 'Password is too weak. Please use at least 6 characters.';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Network error. Please check your internet connection and try again.';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Too many sign-up attempts. Please try again later.';
            break;
          default:
            errorMessage = err.message || 'Failed to create account. Please try again.';
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setLoading(false);
      isSigningUpRef.current = false;
    }
  };

  // Don't show form if user is already authenticated and not in the middle of signup
  if (user && !loading && !authLoading) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50/30 via-white to-green-50/20 py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-emerald-50/20 to-green-50/10 blur-3xl -top-20 -right-20" />
        <div className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-stone-50/15 to-amber-50/10 blur-3xl -bottom-20 -left-20" />
      </div>

      {/* Home Button */}
      <Link
        to="/"
        className="absolute top-4 left-4 sm:top-6 sm:left-6 z-20 flex items-center gap-2 hover:opacity-80 transition-opacity"
      >
        <img
          src={`${process.env.PUBLIC_URL || ''}/logo192.png`}
          alt="Spirara"
          className="w-10 h-10 sm:w-12 sm:h-12"
        />
      </Link>

      <div className="relative z-10 max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-light text-gray-900 mb-2 tracking-tight">
            Join Spirara
          </h1>
          <p className="text-lg text-gray-600 font-light mb-4">
            Start your restorative journey
          </p>
          <div className="mt-6 p-4 bg-emerald-50/50 rounded-lg border border-emerald-100">
            <p className="text-sm text-gray-700 leading-relaxed">
              <strong className="text-emerald-800">Spirara Host Portal</strong> – Turn your off-grid property into a restorative retreat. Connect with travelers seeking digital detox and mental recovery. Set your own rates, manage bookings easily, and make a meaningful impact while earning income.
            </p>
          </div>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8 sm:p-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-lg bg-red-50 border-2 border-red-300 p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800">{error}</p>
                    {error.includes('already registered') && (
                      <Link
                        to="/login"
                        className="mt-2 inline-block text-sm text-red-700 underline hover:text-red-900"
                      >
                        Go to sign in →
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
                  Full name
                </label>
                <input
                  id="displayName"
                  name="displayName"
                  type="text"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                  placeholder="John Doe"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white/50"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={6}
                />
                <p className="mt-1 text-xs text-gray-500">Minimum 6 characters</p>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full group bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-4 rounded-lg text-base font-medium shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:shadow-emerald-300/50 transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                'Creating account...'
              ) : (
                <>
                  Create account
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-emerald-700 hover:text-emerald-800 transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
